import { useCallback, useEffect, useState } from 'react';
import { peopleGateway } from '../../index';
import type { AdminStateTaxResponse } from '../../types';

export interface AdminStateTaxState {
  data: AdminStateTaxResponse | null;
  /** True when the read failed; the panel shows a retryable message. */
  failed: boolean;
  reload: () => void;
}

/** EPN-245: the administrator's read-only state income tax picture for one employee, loaded once per Step 2 visit and shared by State Income Tax,
 * Tax Settings and Tax Jurisdiction Summary. Never throws: a failure is `failed` so each panel can say so and offer Retry. */
export function useAdminStateTax(employeeId: string): AdminStateTaxState {
  const [data, setData] = useState<AdminStateTaxResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    peopleGateway
      .getAdminStateTax(employeeId)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setFailed(false);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId, attempt]);

  const reload = useCallback(() => {
    setData(null);
    setFailed(false);
    setAttempt((n) => n + 1);
  }, []);

  return { data, failed, reload };
}
